/**
 * Meta Pixel Utility for SPA - STANDARD EVENTS ONLY
 * 
 * ENV VAR: REACT_APP_META_PIXEL_ID
 * 
 * IMPORTANT: Uses ONLY standard Meta events:
 * - fbq('track', 'PageView')  - STANDARD
 * - fbq('track', 'Lead')      - STANDARD
 * 
 * NO trackCustom, NO custom parameters that could cause "custom event" status.
 * 
 * TEST PLAN:
 * 1. Go to Meta Events Manager → Test Events
 * 2. Open: https://popup.edytandbloom.de/?debug_pixel=1
 * 3. Check console for pixel debug logs
 * 4. Confirm PageView appears as STANDARD "PageView" (not custom)
 * 5. Submit form with test email
 * 6. Confirm Lead appears as STANDARD "Lead"
 */

const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

// Debug mode
const isDebugMode = () => {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has('debug_pixel');
};

const debugLog = (...args) => {
  if (isDebugMode()) {
    console.log('[Meta Pixel Debug]', ...args);
  }
};

// Track initialization state
let pixelInitialized = false;
let lastPageViewPath = '';
let lastPageViewTime = 0;
const PAGE_VIEW_DEBOUNCE_MS = 500;

/**
 * Initialize Meta Pixel (call once on app mount)
 */
export const initMetaPixel = () => {
  if (pixelInitialized) {
    debugLog('Pixel already initialized, skipping');
    return;
  }

  if (!META_PIXEL_ID) {
    console.warn('[Meta Pixel] REACT_APP_META_PIXEL_ID not set');
    return;
  }

  if (typeof window.fbq !== 'function') {
    console.warn('[Meta Pixel] fbq not loaded');
    return;
  }

  // Initialize pixel with ID from env var - STANDARD INIT
  window.fbq('init', META_PIXEL_ID);
  pixelInitialized = true;
  
  debugLog('Pixel initialized with ID:', META_PIXEL_ID);

  // Fire initial STANDARD PageView
  fireStandardPageView(window.location.pathname);
};

/**
 * Fire STANDARD PageView event
 * Internal function - no custom parameters
 */
const fireStandardPageView = (path) => {
  if (typeof window.fbq !== 'function') return;
  
  // STANDARD EVENT - NO PARAMETERS
  window.fbq('track', 'PageView');
  debugLog('STANDARD PageView fired for path:', path);
};

/**
 * Track PageView (STANDARD event)
 * Call on route changes in SPA
 */
export const trackPageView = (path) => {
  if (typeof window.fbq !== 'function') {
    debugLog('fbq not available for PageView');
    return;
  }

  if (!pixelInitialized) {
    debugLog('Pixel not initialized, skipping PageView');
    return;
  }

  const now = Date.now();
  
  // Deduplicate: same path within debounce window
  if (path === lastPageViewPath && (now - lastPageViewTime) < PAGE_VIEW_DEBOUNCE_MS) {
    debugLog('PageView deduplicated for path:', path);
    return;
  }

  lastPageViewPath = path;
  lastPageViewTime = now;

  // Fire STANDARD PageView - NO PARAMETERS
  fireStandardPageView(path);
};

/**
 * Track Lead (STANDARD event)
 * Call on successful newsletter/form submission
 * NO CUSTOM PARAMETERS - pure standard Lead event
 */
export const trackLead = () => {
  if (typeof window.fbq !== 'function') {
    debugLog('fbq not available for Lead');
    return;
  }

  if (!pixelInitialized) {
    debugLog('Pixel not initialized, skipping Lead');
    return;
  }

  // Deduplicate: check sessionStorage
  const leadKey = 'meta_lead_fired';
  const lastFired = sessionStorage.getItem(leadKey);
  const now = Date.now();
  
  // Prevent duplicate within 30 minutes
  if (lastFired && (now - parseInt(lastFired, 10)) < 30 * 60 * 1000) {
    debugLog('Lead deduplicated');
    return;
  }

  // STANDARD Lead event - NO PARAMETERS
  window.fbq('track', 'Lead');

  // Mark as fired
  sessionStorage.setItem(leadKey, now.toString());
  
  debugLog('STANDARD Lead fired');
};

/**
 * Get pixel status (for debugging)
 */
export const getPixelStatus = () => ({
  pixelId: META_PIXEL_ID,
  initialized: pixelInitialized,
  fbqAvailable: typeof window.fbq === 'function',
  debugMode: isDebugMode()
});

export default {
  initMetaPixel,
  trackPageView,
  trackLead,
  getPixelStatus
};
